import type { PixelProvider, StorefrontPixelConfigDto } from '@xshop/shared';
import { storeHeaders } from '../../api/client';

const CONFIG_PATH = '/api/v1/pixels/storefront';

// The pixels one store loads, grouped so each vendor is installed once with all of its ids.
export type PixelIdsByProvider = Partial<Record<PixelProvider, string[]>>;

// What the storefront needs to decide what to load and what the server already sends itself.
export interface StorefrontPixelSetup {
  ids: PixelIdsByProvider;
  serverSideProviders: string[];
}

let inflight: Promise<StorefrontPixelSetup> | null = null;

// Groups the flat pixel list by provider, keeping the order the API returned them in.
function groupByProvider(config: StorefrontPixelConfigDto): PixelIdsByProvider {
  const grouped: PixelIdsByProvider = {};
  for (const pixel of config.pixels) {
    (grouped[pixel.provider] ??= []).push(pixel.externalId);
  }
  return grouped;
}

// Reads the store's browser pixel configuration once per page load; a failure loads no pixels.
export function loadPixelSetup(): Promise<StorefrontPixelSetup> {
  inflight ??= fetch(CONFIG_PATH, { headers: storeHeaders() })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((json: { data?: StorefrontPixelConfigDto }) => {
      const data = json.data;
      if (!data) throw new Error('Malformed pixel configuration');
      return { ids: groupByProvider(data), serverSideProviders: data.serverSideProviders };
    })
    .catch(() => ({ ids: {}, serverSideProviders: [] }));
  return inflight;
}
